/* eslint-disable react/destructuring-assignment */
/* eslint-disable react/no-danger */
// importer la librairie de react
import React, { useEffect, useImperativeHandle, useRef, useState } from "react"
import { Editor, EditorState, RichUtils, Modifier, CompositeDecorator } from "draft-js"
import { Button, CardContent } from "@mui/material"
import LinkIcon from "@mui/icons-material/Link"
import { stateToHTML } from "draft-js-export-html"
import { stateFromHTML } from "draft-js-import-html"
import Alert from "@mui/material/Alert"
import clsx from "clsx"
import { sanitize } from "dompurify"
import { t } from "i18next"
import FormFieldService from "../../../services/formField"
import { PageConfig, CwComponentConfig } from "../../../models/pageConfig"
import UIService from "../../../services/UIService"
import StoreService from "../../../services/store"
import ActionService from "../../../services/action"
import DialogService from "../../../services/dialog"
import CwColorPicker from "./cw-colorPicker"
import { DialogType } from "../../../models/dialog"

function findLinkEntities(contentBlock: any, callback: any, contentState: any) {
    contentBlock.findEntityRanges((character: any) => {
        const entityKey = character.getEntity()
        return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK"
    }, callback)
}

const LinkSpan = (props: any) => {
    const { url } = props.contentState.getEntity(props.entityKey).getData()
    return (
        <a
            href={url}
            className="cwEditorLink"
            onClick={(e) => { e.preventDefault(); window.open(url, "_blank", "noopener,noreferrer") }}
        >
            {props.children}
        </a>
    )
}

const linkDecorator = new CompositeDecorator([
    { strategy: findLinkEntities, component: LinkSpan },
])

export interface CwEditorHandle {
    insertToken: (token: string) => void
}

const CwEditor = React.forwardRef<
    CwEditorHandle,
    PageConfig<CwComponentConfig.Editor> & { onChange?: (v: string) => void }
>((props, ref) => {
    const { config, name } = props
    // création de toute les variables à partir des config
    const {
        fieldName,
        edit,
        value,
        formLink,
        noVerticalCenterText,
        useRefreshListener,
        readOnly,
        noSnapshot,
        classNameEditor,
    } = config
    const className = config.className ? config.className : ""
    // contenue de l'editor à un instant T (peut etre string si readOny sinon EditorState)
    const [editorState, setEditorState] = useState<any>(
        EditorState.createEmpty(linkDecorator)
    )
    const [error, setError] = useState<{
        hasError: boolean
        helperText?: string
    }>({ hasError: false, helperText: "" })
    const snapshot = useRef<string | undefined>(value)
    const lastHtmlValueRef = useRef<string>(value || "")
    /**
     * Charge la valeur par defaut à mettre dans l'editeur si disponible
     */
    const loadEditorWithValue = (val: string) => {
        let nextEditorState: any = val
        if (!readOnly) {
            nextEditorState = EditorState.set(
                EditorState.createWithContent(
                    stateFromHTML(val, {
                        // fonction de transformation de la template en object State
                        // (gestion des colorations du texte)
                        customInlineFn: (element: any, { Style }) => {
                            // les couleurs sont appliquée sur des balises span
                            if (
                                element.tagName === "SPAN" &&
                                element.style?.color !== ""
                            ) {
                                let styleLabel = null
                                // exploration des couleurs pour voir si elles matchent avec la palette
                                UIService.getInstance()
                                    .getColorPalette()
                                    .palette.forEach((color) => {
                                        // si la couleurs de la span match en format hexa ou rgb
                                        if (
                                            color.rgb === element.style.color ||
                                            color.hex === element.style.color
                                        ) {
                                            // on save le label du style à appliquer dans Draftjs
                                            styleLabel = color.label
                                        }
                                    })
                                return styleLabel ? Style(styleLabel) : null
                            }
                            return null
                        },
                    })
                ),
                { decorator: linkDecorator }
            )
        }
        setEditorState(nextEditorState)
        lastHtmlValueRef.current = val
        // snapshot pour retourner à l'état initial en cas d'annulation
        snapshot.current = val
        // synchro Store pour partage de données
        StoreService.getInstance().local.set(
            fieldName || `inputEditor${name}`,
            val
        )
    }

    const init = () => {
        if (props.actions?.length) {
            // sinon on récupère la valeur en base de données
            ActionService.exec(props.actions as any[]).then((result) => {
                const newValue = result && result !== null ? result : ""
                loadEditorWithValue(newValue)
            })
        } else {
            loadEditorWithValue(value || "")
        }
    }

    useEffect(() => {
        let subId: string | null = null
        // on s'abonne aux changements de route
        if (useRefreshListener)
            subId = DialogService.getInstance().subscribe(
                "pageRefesh",
                DialogType.REFRESH,
                props.name,
                () => {
                    init()
                }
            )
        // premier init
        init()

        return () => {
            if (subId) DialogService.getInstance().unsubscribe(subId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!noSnapshot && typeof snapshot.current !== "undefined")
            loadEditorWithValue(snapshot.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [edit])

    useEffect(() => {
        const nextValue = value || ""
        if (nextValue === lastHtmlValueRef.current) return
        loadEditorWithValue(nextValue)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    /**
     * CallBack de changement de valeur de l'editor
     * @param param EditorState
     */
    const onChangeValue = (param: EditorState) => {
        // set object interne draft-js
        setEditorState(param)
        // export to HTML
        const newHtmlValue = stateToHTML(
            param.getCurrentContent(),
            UIService.getInstance().getColorPalette().colorHtmlMappingExport
        )
        lastHtmlValueRef.current = newHtmlValue
        // synchro Store pour partage de données
        StoreService.getInstance().local.set(
            fieldName || `inputEditor${name}`,
            newHtmlValue
        )
        // on lance l'event pour les composant pères (hors form)
        if (props.onChange) props.onChange(newHtmlValue)
        if (fieldName) {
            const currentSate = FormFieldService.getInstance().send(
                formLink,
                fieldName,
                newHtmlValue
            )
            if (currentSate && currentSate.error !== error.hasError) {
                setError({
                    hasError: currentSate.error,
                    helperText: currentSate.msg,
                })
            }
        }
    }

    /**
     * CallBack d'appliquation du style à une partie du texte séléctionné
     * @param style nom du stype à appliquer "BOLD" | "ITALIC" | "UNDERLINE"
     */
    const onInlineStyle = (style: string) => {
        onChangeValue(RichUtils.toggleInlineStyle(editorState, style))
    }

    /**
     * CallBack d'application du style à un bloc de text
     * @param style "header-one" | "header-two" | "header-three" | "header-four"
     */
    const onBlockStyle = (style: string) => {
        onChangeValue(RichUtils.toggleBlockType(editorState, style))
    }

    /**
     * Applique une couleur à du texte séléctionné
     * @param selectedColor UIService.getInstance().getColorPalette()
     */
    const onChangeColor = (selectedColor: any) => {
        const selection = editorState.getSelection()
        // désactiver la couleur courante sur le texte
        const nextContentState = Object.keys(
            UIService.getInstance().getColorPalette().colorStyleMap
        ).reduce(
            (contentState, color) => {
                return Modifier.removeInlineStyle(
                    contentState,
                    selection,
                    color
                )
            },

            editorState.getCurrentContent()
        )

        // state neutre
        let nextEditorState = EditorState.push(
            editorState,
            nextContentState,
            "change-inline-style"
        )

        const currentStyle = editorState.getCurrentInlineStyle()

        if (selection.isCollapsed()) {
            nextEditorState = currentStyle.reduce((s: any, color: string) => {
                if (s && color) {
                    return RichUtils.toggleInlineStyle(s, color)
                }
                return editorState
            }, nextEditorState)
        }
        if (!currentStyle.has(selectedColor)) {
            nextEditorState = RichUtils.toggleInlineStyle(
                nextEditorState,
                selectedColor
            )
        }

        onChangeValue(nextEditorState)
    }

    /**
     * Ajoute un lien (entity LINK) sur la sélection courante
     */
    const onAddLink = () => {
        if (
            !editorState ||
            typeof (editorState as any).getSelection !== "function"
        )
            return
        const selection = (editorState as EditorState).getSelection()
        if (!selection || selection.isCollapsed()) return

        const url = window.prompt("URL", "https://")
        if (!url) return

        const contentState = (editorState as EditorState).getCurrentContent()
        const contentStateWithEntity = contentState.createEntity(
            "LINK",
            "MUTABLE",
            { url }
        )
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

        const nextEditorState = EditorState.set(editorState as EditorState, {
            currentContent: contentStateWithEntity,
        })

        onChangeValue(
            RichUtils.toggleLink(nextEditorState, selection, entityKey)
        )
    }

    useImperativeHandle(ref, () => ({
        insertToken: (token: string) => {
            const es = editorState as EditorState
            const selection = es.getSelection()
            const content = es.getCurrentContent()
            const newContent = Modifier.insertText(content, selection, token)
            const nextState = EditorState.push(es, newContent, "insert-characters")
            onChangeValue(EditorState.forceSelection(nextState, newContent.getSelectionAfter()))
        },
    }))

    const DraftJSEditor = Editor as unknown as React.ComponentType<any>

    const renderEditMode = () => {
        return (
            <>
                {edit ? (
                    <>
                        <div className="cwEditorToolbar">
                            <CardContent>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onInlineStyle("BOLD")}
                                >
                                    B
                                </Button>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onInlineStyle("ITALIC")}
                                >
                                    I
                                </Button>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onInlineStyle("UNDERLINE")}
                                >
                                    U
                                </Button>
                                <Button
                                    className="styleEditorButton"
                                    onClick={onAddLink}
                                >
                                    <LinkIcon />
                                </Button>
                            </CardContent>
                            <CardContent>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onBlockStyle("header-one")}
                                >
                                    H1
                                </Button>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onBlockStyle("header-two")}
                                >
                                    H2
                                </Button>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onBlockStyle("header-three")}
                                >
                                    H3
                                </Button>
                                <Button
                                    className="styleEditorButton"
                                    onClick={() => onBlockStyle("header-four")}
                                >
                                    H4
                                </Button>
                            </CardContent>
                            <CardContent className="m-2">
                                <div className="colorPickerDiv">
                                    <span className="m-1">
                                        {t("textColor")}
                                    </span>
                                    <CwColorPicker
                                        name="editorColorPicker"
                                        type="colorPicker"
                                        config={{
                                            fieldName: "editorColor",
                                            edit: true,
                                        }}
                                        onChange={onChangeColor}
                                    />
                                </div>
                            </CardContent>
                        </div>
                    </>
                ) : null}
                <div
                    className={clsx(classNameEditor || "editorDiv", {
                        textAreaBorder: edit,
                        editTextStart: noVerticalCenterText,
                    })}
                >
                    <DraftJSEditor
                        readOnly={!edit}
                        editorKey={fieldName}
                        customStyleMap={
                            UIService.getInstance().getColorPalette()
                                .colorStyleMap
                        }
                        editorState={editorState}
                        onChange={(editorStateValue: EditorState) =>
                            onChangeValue(editorStateValue)
                        }
                        spellCheck
                    />
                </div>
                {edit && error.hasError ? (
                    <Alert severity="error">{error.helperText}</Alert>
                ) : null}
            </>
        )
    }

    return (
        <div className={className}>
            {readOnly ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: sanitize(editorState),
                    }}
                />
            ) : (
                renderEditMode()
            )}
        </div>
    )
})

export default CwEditor
